import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ndfApi } from '../../services/ndf-api';
import { BoutonSecondaire, Champ, Selection } from './ui/Champ';

/**
 * Récapitulatif annuel des indemnités kilométriques (R-IK-07).
 *
 * Deux formats, deux usages : le PDF s'archive et se transmet tel quel — c'est
 * la pièce qu'on présente ; le tableur se retravaille — un comptable trie,
 * filtre et recoupe avec sa propre comptabilité. N'en fournir qu'un condamne
 * l'autre usage à la ressaisie.
 *
 * `salarieId` absent : c'est le sien. Renseigné : celui d'un collaborateur, ce
 * que le serveur n'autorise qu'à qui peut voir sa fiche.
 */
export function RecapitulatifIk({ salarieId }: { salarieId?: string }) {
  const annees = Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - i);
  const [annee, setAnnee] = useState(annees[0]!);

  const parametres = `annee=${annee}${salarieId ? `&salarie_id=${salarieId}` : ''}`;
  const recap = useQuery({
    queryKey: ['ndf', 'recapitulatif-ik', annee, salarieId ?? 'moi'],
    queryFn: () => ndfApi.trajets.recapitulatif(annee, salarieId),
    retry: false,
  });

  const donnees = recap.data?.recapitulatif;
  const vide = donnees && donnees.lignes.length === 0;

  return (
    <section className="space-y-3 rounded-lg border border-nuit/10 bg-white p-4">
      <h2 className="font-titre text-sm font-bold text-nuit">Récapitulatif kilométrique</h2>

      <Champ label="Année">
        <Selection value={annee} onChange={(e) => setAnnee(Number(e.target.value))}>
          {annees.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </Selection>
      </Champ>

      {recap.isLoading && <p className="text-sm text-nuit/60">Chargement…</p>}

      {vide && (
        <p className="text-sm text-nuit/70">Aucun trajet déclaré en {annee}.</p>
      )}

      {donnees && !vide && (
        <>
          <dl className="space-y-1 text-sm">
            <Ligne libelle="Trajets déclarés" valeur={String(donnees.lignes.length)} />
            <Ligne libelle="Distance totale" valeur={`${donnees.total_km} km`} />
            {donnees.cumul_initial_km > 0 && (
              <Ligne
                libelle="Kilométrage repris au 1er janvier"
                valeur={`${donnees.cumul_initial_km} km`}
              />
            )}
            <Ligne libelle="Cumul annuel retenu" valeur={`${donnees.cumul_final_km} km`} />
            <Ligne
              libelle="Indemnités"
              valeur={donnees.total_montant.toLocaleString('fr-FR', {
                style: 'currency',
                currency: 'EUR',
              })}
            />
          </dl>

          {/* Le seuil n'interdit rien : il annonce ce qui pourra être demandé. */}
          {donnees.depasse_seuil && (
            <p className="rounded-md border border-jaune-fond/60 border-l-4 border-l-jaune-fond p-3 text-sm text-jaune">
              Au-delà de 4 500 km déclarés dans l’année, un justificatif d’usage professionnel du
              véhicule peut vous être demandé.
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <a
              href={`/api/ndf/trajets/recapitulatif.pdf?${parametres}`}
              target="_blank"
              rel="noreferrer"
              className="flex min-h-11 items-center rounded-lg border border-nuit/20 px-4 text-sm font-medium text-nuit"
            >
              Télécharger en PDF
            </a>
            <a
              href={`/api/ndf/trajets/recapitulatif.xlsx?${parametres}`}
              className="flex min-h-11 items-center rounded-lg border border-nuit/20 px-4 text-sm font-medium text-nuit"
            >
              Télécharger en tableur
            </a>
          </div>
        </>
      )}
    </section>
  );
}

function Ligne({ libelle, valeur }: { libelle: string; valeur: string }) {
  return (
    <div className="flex justify-between gap-4 border-b border-nuit/5 pb-1 last:border-0">
      <dt className="text-nuit/60">{libelle}</dt>
      <dd className="text-right font-medium text-nuit">{valeur}</dd>
    </div>
  );
}
